// Place search for the planning map, from whichever source this build has.
//
//   local index  A static list of Turkish settlements, lakes, dams, peaks and airfields, baked and
//                served alongside the zone data. No ceiling, no key, no round trip per keystroke.
//   Nominatim    OpenStreetMap's geocoder. Free and CORS-enabled, but their usage policy caps an
//                ENTIRE APPLICATION at ~1 request/second summed across all its users
//                (operations.osmfoundation.org/policies/nominatim/). That is fine for one person
//                running a local tool and a breach for a public site, so it is the fallback for
//                builds with no index rather than the default.
//
// The two are not equivalent and the difference is deliberate: Nominatim also matches streets and
// addresses, the local index matches named places. A flight is planned around a village, a lake or
// a peak, so the loss is small — and it buys search that answers while you are still typing.

import { fetchPlaceIndexFile } from './fetch.ts';

export interface GeoResult {
  /** Stable within one result list. Two villages can share a name AND a province, so the list
   *  cannot be keyed by name — "Yeniköy" alone occurs 225 times in Türkiye. */
  id: string;
  /** Display name, qualified by province where one is known: "Gerede, Bolu". */
  name: string;
  lat: number;
  lon: number;
}

/**
 * Optional origin serving the baked place index, as `<origin>/<file named by the manifest>`.
 * Unset (drone-player's default) falls back to Nominatim, unchanged.
 */
const SNAPSHOT: string | undefined =
  (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_ZONE_SNAPSHOT || undefined;

/**
 * Fold a Turkish string to a comparable key.
 *
 * Two separate steps, and doing only one of them is the usual bug. Case first, with the dotted/
 * dotless pair handled explicitly, because `toLowerCase()` maps 'I' to 'i' rather than 'ı' unless
 * the Turkish locale is requested — and `toLocaleLowerCase('tr')` on every keystroke over 61k rows
 * is slower than a two-character replace. Then diacritics, so someone typing "mugla" on a keyboard
 * without Turkish layout still finds Muğla.
 */
const FOLD: Record<string, string> = { İ: 'i', I: 'ı', ı: 'i', ş: 's', ğ: 'g', ü: 'u', ö: 'o', ç: 'c', â: 'a', î: 'i', û: 'u' };
export function fold(s: string): string {
  let out = '';
  for (const ch of s) out += FOLD[ch] ?? ch;
  return out.toLowerCase().replace(/[ışğüöçâîû]/g, (c) => FOLD[c] ?? c);
}

export interface PlaceIndex {
  attribution: string;
  provinces: string[];
  /** [name, lat, lon, provinceIdx, tier, population], pre-sorted by rank at build time. */
  places: Array<[string, number, number, number, number, number]>;
  /** Folded names, computed once on load — never shipped, since it doubles the payload. */
  keys: string[];
}

let indexPromise: Promise<PlaceIndex> | null = null;

/**
 * Load the index on FIRST SEARCH, not at startup.
 *
 * It is ~0.7 MB compressed, and most visitors open the map to look at it rather than to search. The
 * first keystroke pays for it once; every later one is a memory scan.
 *
 * Deliberately NOT tied to the caller's abort signal. Each keystroke aborts the previous search, and
 * a shared cached resource must not be cancelled because the query that happened to trigger it was
 * superseded — the load never finishes while the user is still typing, and the last keystroke
 * inherits a promise already rejected by an earlier abort, so search fails exactly for people who
 * type at a normal speed. Aborting the SEARCH is still correct and still happens: it is a synchronous
 * scan once this resolves, and the caller drops a result whose signal fired.
 */
async function loadIndex(file: string): Promise<PlaceIndex> {
  if (!indexPromise) {
    indexPromise = (async () => {
      const r = await fetch(`${SNAPSHOT}/${file}`);
      if (!r.ok) throw new Error(`place index: HTTP ${r.status}`);
      const raw = (await r.json()) as Omit<PlaceIndex, 'keys'>;
      return { ...raw, keys: raw.places.map((p) => fold(p[0])) };
    })().catch((e: unknown) => {
      indexPromise = null; // let the next keystroke retry rather than fail forever
      throw e;
    });
  }
  return indexPromise;
}

const LIMIT = 8;

/**
 * Rank-ordered prefix search over the loaded index.
 *
 * Word-start matching, not substring: typing "kar" should offer Karabük and Karaman, not every
 * village with "kar" buried in the middle. Because the index is pre-sorted by rank, a scan can stop
 * once it has enough — but only for prefix hits, since a later exact match would still outrank an
 * earlier partial one. So exact matches get their own pass.
 *
 * That separation used to be described here and not implemented: both kinds were collected in ONE
 * loop whose stop condition counted only prefix hits, so an exact match ranked below the 32nd
 * prefix hit was never reached. The names it loses are the ones most likely to be typed — a query
 * that is also a common prefix. A settlement literally named "Kara" sits behind every "Kara…" above
 * it in rank, and typing its exact name did not offer it at all.
 */
export function search(idx: PlaceIndex, query: string): GeoResult[] {
  const q = fold(query.trim());
  if (q.length < 2) return [];
  // Exact first, and this pass may not stop early on someone else's counter. A full scan of the
  // index is ~61k string comparisons, which is under a millisecond and paid once per keystroke.
  const exact: number[] = [];
  for (let i = 0; i < idx.keys.length && exact.length < LIMIT; i++) if (idx.keys[i] === q) exact.push(i);
  const prefix: number[] = [];
  for (let i = 0; i < idx.keys.length && prefix.length < LIMIT; i++) {
    const k = idx.keys[i];
    if (k !== q && (k.startsWith(q) || k.includes(' ' + q))) prefix.push(i);
  }
  return [...exact, ...prefix].slice(0, LIMIT).map((i) => {
    const [name, lat, lon, prov] = idx.places[i];
    const province = prov >= 0 ? idx.provinces[prov] : undefined;
    return { id: `p${i}`, name: province && province !== name ? `${name}, ${province}` : name, lat, lon };
  });
}

async function geocodeNominatim(q: string, signal?: AbortSignal): Promise<GeoResult[]> {
  const params = new URLSearchParams({ format: 'jsonv2', q, limit: '6', countrycodes: 'tr', 'accept-language': 'tr' });
  const r = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, { signal });
  if (!r.ok) throw new Error(`geocode HTTP ${r.status}`);
  const arr = (await r.json()) as Array<{ place_id?: number; display_name?: string; lat?: string; lon?: string }>;
  return arr
    .map((a, i) => ({ id: String(a.place_id ?? `n${i}`), name: a.display_name ?? '', lat: Number(a.lat), lon: Number(a.lon) }))
    .filter((g) => g.name && Number.isFinite(g.lat) && Number.isFinite(g.lon));
}

/**
 * Which index this origin publishes, asked ONCE per session rather than per keystroke.
 *
 * The manifest read behind this is memoized for only a minute, so consulting it on every keystroke
 * would put a request on the network for every letter typed — the exact cost this module exists to
 * remove. A bake landing mid-session therefore keeps the index already loaded, which is harmless:
 * settlements do not move, and the file is immutable at its versioned name.
 */
let filePromise: Promise<string | null> | null = null;
const placeIndexFile = (): Promise<string | null> => {
  if (filePromise) return filePromise;
  // A null answer is NOT memoized. `fetchPlaceIndexFile` swallows every error and resolves null, so
  // `??=` — which only reassigns when the variable is nullish, and this one holds a Promise — cached
  // "this origin publishes no index" permanently from a single CDN blip. One unlucky first keystroke
  // and place search was dead for the rest of the session, on a build that deliberately never falls
  // back to Nominatim, with nothing but a generic error to show for it.
  const p = fetchPlaceIndexFile().then((f) => {
    if (!f) filePromise = null; // transient or genuinely absent: either way, ask again next time
    return f;
  });
  filePromise = p;
  return p;
};

/**
 * How long to wait after a keystroke before searching.
 *
 * Build-time, and the two values are not a preference. Nominatim's limit is per APPLICATION, so a
 * public build typing at 80 ms would breach it with a single user; a local index has no limit and a
 * half-second wait would just make instant search feel slow.
 */
export const SEARCH_DEBOUNCE_MS = SNAPSHOT ? 80 : 550;

/**
 * A build with a snapshot origin NEVER falls back to Nominatim.
 *
 * That looks like a missing safety net and is the opposite. The fallback would fire exactly when
 * the index is unreachable — a broken origin, which for a public site means every visitor at once,
 * each typing into a geocoder that permits one request per second for the whole site. Reporting
 * search as unavailable is the honest failure; quietly breaching someone else's terms is not.
 */
export async function geocode(query: string, signal?: AbortSignal): Promise<GeoResult[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  if (!SNAPSHOT) return geocodeNominatim(q, signal);
  const file = await placeIndexFile();
  if (!file) throw new Error('place search: this origin publishes no place index');
  const idx = await loadIndex(file);
  return signal?.aborted ? [] : search(idx, q);
}

/** Attribution for the loaded index, for the credit line. Null until a search has loaded it. */
export async function placeAttribution(): Promise<string | null> {
  if (!indexPromise) return null;
  try {
    return (await indexPromise).attribution;
  } catch {
    return null;
  }
}
